import {find as _find, isEmpty as _isEmpty} from 'lodash';

import {getSelectedValues} from '@appComponents/HOCFiltersWrapper';
import {
    GetContentCategoriesByLevelAndParentsForDropdown,
    GetContentCategoriesForDropdown,
} from '@graphql/contentCategory/query';
import {GetSportsWithCourt, GetStageSports, GetStandardSports} from '@graphql/sport/query';
import {GetStadiumsByTournamentsForDropdown} from '@graphql/stadium/query';
import {GetStageCategoriesForDropdown} from '@graphql/stageCategory/query';
import {GetTournamentsForDropdownMultipleCategories} from '@graphql/tournament/query';
import {GetTournamentCategoriesFilteredBySports} from '@graphql/tournamentCategory/query';
import {showMessageBox} from '@utils/messageBox';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';

export const createCascadeFetchers = ({getClient, getForm, getState, setState, messageBoxName}) => {
    const reportFailure = (message, error) => {
        showMessageBox(messageBoxName, message, `${error}`, 'error');
    };

    const fetchStandardSports = (isStandard = true) => (
        getClient().query({
            query: GetStandardSports,
            variables: {isStandard},
        }).then((result) => {
            setState(() => ({
                sportsLoading: false,
                fetchedSports: sortDropdownOptionsAlphanumerically(
                    result.data.sports.mapDataForDropdownWithIntVal()
                ),
            }));
        }).catch((error) => {
            setState(() => ({
                sportsLoading: false,
                fetchedSports: [],
            }));

            reportFailure('Failed to fetch sports filter data', error);
        })
    );

    const fetchStageSports = (isStage = true) => (
        getClient().query({
            query: GetStageSports,
            variables: {isStage},
        }).then((result) => {
            setState(() => ({
                stageSportsLoading: false,
                fetchedStageSports: sortDropdownOptionsAlphanumerically(
                    result.data.sports.mapDataForDropdownWithIntVal()
                ),
            }));
        }).catch((error) => {
            setState(() => ({
                stageSportsLoading: false,
                fetchedStageSports: [],
            }));

            reportFailure('Failed to fetch sports filter data', error);
        })
    );

    const fetchTournamentCategories = (sportIds) => (
        getClient().query({
            query: GetTournamentCategoriesFilteredBySports,
            variables: {
                sports: sportIds,
            },
        }).then((result) => {
            const tournamentCategories = sortDropdownOptionsAlphanumerically(
                    result.data.tournamentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedTournamentCategories = getState().selectedTournamentCategories || [];

            setState(() => ({
                selectedSports: sportIds,
                tournamentCategoriesLoading: false,
                tournamentCategories,
            }));

            getForm().changeValue(
                'tournamentCategories',
                getSelectedValues(selectedTournamentCategories, tournamentCategories)
            );
        }).catch((error) => {
            setState(() => ({
                tournamentCategoriesLoading: false,
                tournamentCategories: [],
            }));

            reportFailure('Failed to fetch tournament categories filter data', error);
        })
    );

    const fetchTournaments = (tournamentCategoryIds) => (
        getClient().query({
            query: GetTournamentsForDropdownMultipleCategories,
            variables: {
                categories: tournamentCategoryIds,
            },
        }).then((result) => {
            const tournaments = sortDropdownOptionsAlphanumerically(
                result.data.tournaments.mapDataForDropdownWithIntVal()
            );

            setState(() => ({
                tournamentsLoading: false,
                tournaments,
            }));

            getForm().changeValue(
                'tournaments',
                getSelectedValues(getState().selectedTournaments || [], tournaments)
            );
        }).catch((error) => {
            setState(() => ({
                tournamentsLoading: false,
                tournaments: [],
            }));

            reportFailure('Failed to fetch tournaments filter data', error);
        })
    );

    const fetchCourts = (tournamentIds) => (
        getClient().query({
            query: GetStadiumsByTournamentsForDropdown,
            variables: {
                tournaments: tournamentIds,
            },
        }).then((result) => {
            const courts = sortDropdownOptionsAlphanumerically(
                    result.data.stadiums.mapDataForDropdownWithIntVal()
                ),
                selectedCourts = getState().selectedCourts || [];

            setState(() => ({
                courtsLoading: false,
                courts,
            }));

            getForm().changeValue('court', getSelectedValues(selectedCourts, courts));
        }).catch((error) => {
            setState(() => ({
                courtsLoading: false,
                courts: [],
            }));

            reportFailure('Failed to fetch courts filter data', error);
        })
    );

    const loadCourtsForTournaments = (sportIds, tournamentIds, {scheduleCourtsFetch}) => {
        if (_isEmpty(sportIds) || _isEmpty(tournamentIds)) {
            return null;
        }

        return getClient().query({
            query: GetSportsWithCourt,
        }).then((result) => {
            const sportsWithCourt = result.data.sports,
                hasCourtSport = sportIds.some((sportId) => _find(sportsWithCourt, {id: sportId.toString()}));

            if (!hasCourtSport) {
                return;
            }

            scheduleCourtsFetch(tournamentIds.map(Number));
            setState(() => ({
                hasSelectedTournaments: true,
                selectedTournaments: tournamentIds,
                courtsLoading: true,
            }));
        }).catch((error) => {
            reportFailure('Failed to handle tournaments filter dropdown change', error);
        });
    };

    const fetchSecondStageCategories = (firstStageCategoryIds) => {
        const previousSelection = (getState().selectedSecondStageCategories || []).map(Number);

        setState(() => ({secondStageCategoriesLoading: true}));

        return getClient().query({
            query: GetStageCategoriesForDropdown,
            variables: {
                sports: null,
                parents: firstStageCategoryIds,
            },
        }).then((result) => {
            const secondStageCategories = sortDropdownOptionsAlphanumerically(
                    result.data.stageCategories.mapDataForDropdownWithIntVal()
                ),
                selection = getSelectedValues(previousSelection, secondStageCategories);

            setState(() => ({
                secondStageCategories,
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: selection,
            }));

            getForm().changeValue('secondStageCategories', _isEmpty(selection) ? null : selection);
        }).catch((error) => {
            setState(() => ({
                secondStageCategories: [],
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: [],
            }));
            getForm().changeValue('secondStageCategories', null);

            reportFailure('Failed to fetch second stage categories', error);
        });
    };

    const fetchFirstStageCategories = (sportIds, {cancelPendingSecondStageFetch, reloadSecondStageCategories}) => {
        const previousSelection = (getState().selectedFirstStageCategories || []).map(Number);

        setState(() => ({firstStageCategoriesLoading: true}));

        return getClient().query({
            query: GetStageCategoriesForDropdown,
            variables: {
                sports: sportIds,
                parents: null,
            },
        }).then((result) => {
            const firstStageCategories = sortDropdownOptionsAlphanumerically(
                    result.data.stageCategories.mapDataForDropdownWithIntVal()
                ),
                selection = getSelectedValues(previousSelection, firstStageCategories);

            setState(() => ({
                firstStageCategories,
                firstStageCategoriesLoading: false,
                selectedFirstStageCategories: selection,
            }));

            getForm().changeValue('firstStageCategories', _isEmpty(selection) ? null : selection);

            cancelPendingSecondStageFetch();

            if (_isEmpty(selection)) {
                setState(() => ({
                    secondStageCategories: [],
                    secondStageCategoriesLoading: false,
                    selectedSecondStageCategories: [],
                }));
                getForm().changeValue('secondStageCategories', null);
            } else {
                setState(() => ({secondStageCategoriesLoading: true}));
                reloadSecondStageCategories(selection);
            }
        }).catch((error) => {
            setState(() => ({
                firstStageCategories: [],
                firstStageCategoriesLoading: false,
                selectedFirstStageCategories: [],
                secondStageCategories: [],
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: [],
            }));
            getForm().changeValue('firstStageCategories', null);
            getForm().changeValue('secondStageCategories', null);

            reportFailure('Failed to fetch stage categories', error);
        });
    };

    const fetchContentCategoriesLevel1 = () => (
        getClient().query({
            query: GetContentCategoriesForDropdown,
            variables: {
                level: 1,
            },
        }).then((result) => {
            const contentCategoriesFirstLevel = sortDropdownOptionsAlphanumerically(
                    result.data.contentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedFirstLevelCategories = getState().selectedFirstLevelCategories || [];

            setState(() => ({
                contentCategoriesFirstLevelLoading: false,
                contentCategoriesFirstLevel,
            }));

            getForm().changeValue(
                'contentCategoriesLevel1',
                getSelectedValues(selectedFirstLevelCategories, contentCategoriesFirstLevel)
            );
        }).catch((error) => {
            setState(() => ({
                contentCategoriesFirstLevelLoading: false,
                contentCategoriesFirstLevel: [],
            }));

            reportFailure('Failed to fetch first level content categories filter data', error);
        })
    );

    const fetchContentCategoriesLevel2 = (firstLevelIds) => (
        getClient().query({
            query: GetContentCategoriesByLevelAndParentsForDropdown,
            variables: {
                level: 2,
                parents: firstLevelIds,
            },
        }).then((result) => {
            const contentCategoriesSecondLevel = sortDropdownOptionsAlphanumerically(
                    result.data.contentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedSecondLevelCategories = getState().selectedSecondLevelCategories || [];

            setState(() => ({
                contentCategoriesSecondLevelLoading: false,
                contentCategoriesSecondLevel,
            }));

            getForm().changeValue(
                'contentCategoriesLevel2',
                getSelectedValues(selectedSecondLevelCategories, contentCategoriesSecondLevel)
            );
        }).catch((error) => {
            setState(() => ({
                contentCategoriesSecondLevelLoading: false,
                contentCategoriesSecondLevel: [],
            }));

            reportFailure('Failed to fetch 2nd level content categories filter data', error);
        })
    );

    const fetchContentCategoriesLevel3 = (secondLevelIds) => (
        getClient().query({
            query: GetContentCategoriesByLevelAndParentsForDropdown,
            variables: {
                level: 3,
                parents: secondLevelIds,
            },
        }).then((result) => {
            const contentCategoriesThirdLevel = sortDropdownOptionsAlphanumerically(
                    result.data.contentCategories.mapDataForDropdownWithIntVal()
                ),
                selectedThirdLevelCategories = getState().selectedThirdLevelCategories || [];

            setState(() => ({
                contentCategoriesThirdLevelLoading: false,
                contentCategoriesThirdLevel,
            }));

            getForm().changeValue(
                'contentCategoriesLevel3',
                getSelectedValues(selectedThirdLevelCategories, contentCategoriesThirdLevel)
            );
        }).catch((error) => {
            setState(() => ({
                contentCategoriesThirdLevelLoading: false,
                contentCategoriesThirdLevel: [],
            }));

            reportFailure('Failed to fetch 3rd level content categories filter data', error);
        })
    );

    return {
        fetchContentCategoriesLevel1,
        fetchContentCategoriesLevel2,
        fetchContentCategoriesLevel3,
        fetchCourts,
        fetchFirstStageCategories,
        fetchSecondStageCategories,
        fetchStageSports,
        fetchStandardSports,
        fetchTournamentCategories,
        fetchTournaments,
        loadCourtsForTournaments,
    };
};
